import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/context/ToastContext';
import GlobalLoader from '@/components/context/GlobalLoader';
import ReferralPanel from '../components/ReferralPanel';
import { Check, CheckCircle, X } from 'lucide-react';
import { trackCustomEvent, trackInitiateCheckout } from '../lib/fbpixel';
import { trackRedditAddToCart } from '../lib/redditPixel';
import {
  phInitiateCheckout,
  phPlanChange,
  phFailure,
} from '../lib/posthog';
import { refreshPostHogIdentity } from '../components/PostHogIdentifier';
import { API_BASE } from '../lib/apiConfig';
import { trackPaymentCompleted, updateIntercom } from '../lib/intercom';
import { getMetaTrackingContext } from '../lib/checkoutContext';
import { startCaseCheckout, type StartCasePlan } from '../lib/freeAccount';
import '@styles/pages/billing.css';
// EOB upgrade section reuses the eob.css design system so /plan and /eob
// look like the same page family for an EOB customer in the upgrade funnel.
import '@styles/pages/eob.css';

/* ----------------------------------
   TYPES
---------------------------------- */
type Subscription = {
  plan: 'one_off' | 'annual' | 'eob' | 'action_plan';
  status: string;
  bills_limit: number;
  current_period_start: string | null;
  current_period_end: string | null;
};

/* ==================================
   PLAN PAGE
================================== */
export default function Plan() {
  const { user, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [billsUsed, setBillsUsed] = useState<number>(0);
  /* Total saved across all closed bills, used for the v3 "Saved on this
     plan" hero metric. Null while loading so we can show a skeleton. */
  const [totalSaved, setTotalSaved] = useState<number | null>(null);
  /* Latest EOB review status — null = none, else 'submitted'/'in_review'/
     'report_ready'. Drives the copy + CTA shown in the EOB-plan card so
     we stop telling users we're still reviewing after the report is done. */
  const [eobStatus, setEobStatus] = useState<
    'submitted' | 'in_review' | 'report_ready' | null
  >(null);
  /* Personalized Action Plan accounts: how many plans have been sent in. Compared with
     bills_limit (plans paid for) to say what is left. */
  const [actionPlanCount, setActionPlanCount] = useState(0);
  /* Personalized Action Plan accounts: is the $49 upgrade credit still open, and until
     when. Null until the server has answered. */
  const [apCredit, setApCredit] = useState<{ eligible: boolean; deadline: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [addonLoading, setAddonLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [downgradeLoading, setDowngradeLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);
  // Retention cancel flow. Step 1 captures a reason, step 2 is a save
  // attempt before we actually cancel. Reset to 'reason' whenever the
  // modal opens.
  const [cancelStep, setCancelStep] = useState<'reason' | 'save'>('reason');
  const [cancelReason, setCancelReason] = useState<string>('');
  const [cancelComment, setCancelComment] = useState<string>('');
  const [successBanner, setSuccessBanner] = useState<{
    title: string;
    desc: string;
  } | null>(null);

  /* Open/reset the cancel flow */
  const openCancelFlow = () => {
    setCancelStep('reason');
    setCancelReason('');
    setCancelComment('');
    setShowDowngradeConfirm(true);
  };

  // Reasons surfaced in the cancel flow. Values map to the server's
  // allowlist in cancelSubscription.ts.
  const CANCEL_REASONS: Array<{ value: string; label: string }> = [
    { value: 'too_expensive', label: 'It\'s too expensive' },
    { value: 'no_bills', label: 'I don\'t have bills to negotiate right now' },
    { value: 'results', label: 'I didn\'t get the results I hoped for' },
    { value: 'one_time', label: 'I only needed help with one bill' },
    { value: 'other', label: 'Something else' },
  ];

  /* Verify addon/upgrade payment on return from Stripe.
     Purchase analytics (Meta pixel/CAPI, Reddit, PostHog, Intercom) ONLY
     fire AFTER /stripe/verify-{addon,upgrade} has confirmed the Stripe
     session payment_status === 'paid'. Previously these fires happened
     based on URL params alone, which let anyone with a /plan?addon=success
     link manufacture a fake Purchase event in Meta. For EOB → Per Bill
     upgrade, the server-side Stripe webhook is the single source of the
     Meta + Reddit CAPI Purchase fire — we no longer fire client-side here
     because there's no synchronous verification endpoint and the webhook
     CAPI fire already captures fbp/fbc from session.metadata. */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const isAddon = params.get('addon') === 'success';
    const isUpgrade = params.get('upgrade') === 'success';
    const isEobUpgrade = params.get('eob_upgrade') === 'success';
    const isActionPlanUpgrade = params.get('action_plan_upgrade') === 'success';
    // Cents the server took off a Personalized Action Plan upgrade (0 outside the 14-day
    // window). Only used to report the right value to Intercom below.
    const creditCents = Number(params.get('credit') ?? 0) || 0;

    // Personalized Action Plan → Pay Per Bill. verify-action-plan-upgrade moves the row
    // to one_off itself (the webhook does too, whichever lands first), and
    // returns the row so the page can switch straight away.
    if (isActionPlanUpgrade) {
      window.history.replaceState({}, '', '/plan');
      setSuccessBanner({
        title: 'Payment successful',
        desc: 'Upload the bill and a specialist takes it from here.',
      });
      showToast('Payment successful! Upload your bill to start.', 'success');

      if (!sessionId) return;

      const verifyActionPlanUpgrade = async () => {
        try {
          const res = await fetch(`${API_BASE}/stripe/verify-action-plan-upgrade`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId }),
          });
          const data = await res.json();
          if (!res.ok || !data.subscription) {
            console.error('verify-action-plan-upgrade failed:', data?.error);
            return;
          }
          setSubscription(data.subscription);
          // Internal analytics only, same reasoning as the EOB branch below.
          trackPaymentCompleted({ plan: 'action_plan_to_one_off', amount: 129 - creditCents / 100 });
          updateIntercom({ plan: 'one_off' });
          refreshPostHogIdentity();
        } catch (err) {
          console.error('verify-action-plan-upgrade error:', err);
        }
      };

      verifyActionPlanUpgrade();
      return;
    }

    // EOB → Per Bill upgrade. Webhook handles the DB transition AND the
    // authoritative server-side Meta + Reddit CAPI Purchase fire. We do
    // ALSO fire client-side analytics — but only after /verify-eob-upgrade
    // confirms `session.payment_status === 'paid'`. Same shape as the
    // addon/upgrade flow below: URL params alone NEVER trigger a Purchase.
    if (isEobUpgrade) {
      window.history.replaceState({}, '', '/plan');
      setSuccessBanner({
        title: 'Payment successful',
        desc: 'Agent Loop is now on the case. Upload your bill and we\'ll get to work.',
      });
      showToast('Payment successful! Upload your bill to start.', 'success');

      if (!sessionId) return;

      const verifyEob = async () => {
        try {
          const res = await fetch(`${API_BASE}/stripe/verify-eob-upgrade`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId }),
          });
          const data = await res.json();
          if (!res.ok || !data.success) {
            console.error('verify-eob-upgrade failed:', data?.error);
            return;
          }

          /* ─── Verified-only purchase analytics ─────────────────────────
             Stripe has confirmed payment. INTERNAL analytics only.

             No Meta / Reddit / GA4 Purchase fire here (2026-07-27). Every
             flow on this page is an EXISTING customer expanding — addon
             bills and upgrades. Reporting those to the ad platforms
             credits expansion revenue back to whatever ad the customer
             clicked months ago, which inflates campaign ROAS and teaches
             the optimizers the wrong thing. Ad platforms now only ever
             see net-new acquisition (webhook.ts fires those). PostHog,
             Intercom and Stripe remain the revenue truth. */
          /* 🚫 2026-08-08 — PostHog `purchase` moved SERVER-SIDE.
             apps/server stripe/webhook.ts::captureServerPurchase is the single
             source now (fires above the verify-endpoint idempotency guard, so
             it lands whichever of the two wins the race). Keeping a client
             copy here would double-count, which is what produced 26 PostHog
             purchase events against 11 Stripe charges over 90 days. */
          trackPaymentCompleted({ plan: 'eob_to_one_off', amount: 60 });
          updateIntercom({ plan: 'one_off' });
          // Keep PostHog person properties as current as Intercom's. Without
          // this, `plan` / `subscription_status` stayed stale on the PostHog
          // profile for the rest of the session after an upgrade.
          refreshPostHogIdentity();
        } catch (err) {
          console.error('verify-eob-upgrade error:', err);
        }
      };

      verifyEob();
      return;
    }

    if (!isAddon && !isUpgrade) return;

    window.history.replaceState({}, '', '/plan');

    if (!sessionId) {
      // No session id means no way to verify. Show a passive banner and
      // exit — DO NOT fire purchase analytics on URL params alone.
      setSuccessBanner({
        title: 'Payment successful',
        desc: isUpgrade
          ? 'Your plan has been upgraded to Annual.'
          : 'Your new bill has been added to your plan.',
      });
      showToast('Payment successful!', 'success');
      return;
    }

    const endpoint = isUpgrade ? 'verify-upgrade' : 'verify-addon';
    const verify = async () => {
      try {
        const res = await fetch(`${API_BASE}/stripe/${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId }),
        });

        const data = await res.json();

        if (res.ok && data.subscription) {
          setSubscription(data.subscription);
          setSuccessBanner({
            title: 'Payment successful',
            desc: isUpgrade
              ? 'Your plan has been upgraded to Annual. You now have 6 bills included.'
              : 'Your new bill has been added to your plan. You can now submit an additional bill.',
          });
          showToast(
            isUpgrade
              ? 'Welcome to the Annual Plan!'
              : 'Payment successful! Your new bill has been added.',
            'success'
          );

          /* ─── Verified-only purchase analytics ─────────────────────────
             The Stripe-side verify endpoint above already enforces
             session.payment_status === 'paid'. INTERNAL analytics only
             (PostHog + Intercom) — see the note in the EOB-upgrade branch
             above for why addon and upgrade purchases are deliberately NOT
             reported to Meta, Reddit or GA4. */
          if (isUpgrade) {
            // The server appends ?from=<previousPlan> to the success URL so
            // we can fire the right purchase value: $499 minus the credit.
            const fromPlan = params.get('from'); // 'one_off' | 'eob' | 'action_plan' | null
            const upgradeValue =
              fromPlan === 'action_plan'
                ? 499 - creditCents / 100 // $49 credit inside 14 days, else full price
                : fromPlan === 'eob'
                  ? 430 // $499 − $69 EOB credit
                  : 370; // default: $499 − $129 one-off credit
          /* 🚫 2026-08-08 — PostHog `purchase` moved SERVER-SIDE.
             apps/server stripe/webhook.ts::captureServerPurchase is the single
             source now (fires above the verify-endpoint idempotency guard, so
             it lands whichever of the two wins the race). Keeping a client
             copy here would double-count, which is what produced 26 PostHog
             purchase events against 11 Stripe charges over 90 days. */
            trackPaymentCompleted({ plan: 'upgrade_to_annual', amount: upgradeValue });
            updateIntercom({ plan: 'annual' });
            refreshPostHogIdentity();
          } else {
            /* ⚠️ 2026-08-08 — REVENUE BUG FIX.
               This used to price the addon at the SUBSCRIPTION's plan price
               (annual → 499, eob → 69, else 129). That is the price of the
               plan the customer already owns, not the price of the extra bill
               they just bought. The real addon charge is set at checkout
               creation a few hundred lines below:
                   const addonValue = 129 (49 for Action Plan accounts)
               So an annual customer adding one bill was recording $499 of
               $revenue in PostHog and $499 in Intercom against a much smaller charge —
               an 8.5x overstatement that corrupted the revenue dashboard and
               any LTV or ROAS analysis built on it.
               Keep this expression in sync with the checkout-creation one. */
            const verifiedPlan: 'annual' | 'one_off' | 'eob' | undefined =
              data.subscription?.plan;
            void verifiedPlan;
            const addonValue = 129;
          /* 🚫 2026-08-08 — PostHog `purchase` moved SERVER-SIDE.
             apps/server stripe/webhook.ts::captureServerPurchase is the single
             source now (fires above the verify-endpoint idempotency guard, so
             it lands whichever of the two wins the race). Keeping a client
             copy here would double-count, which is what produced 26 PostHog
             purchase events against 11 Stripe charges over 90 days. */
            trackPaymentCompleted({ plan: 'addon_bill', amount: addonValue });
          }
        } else {
          console.error(`${endpoint} verify failed:`, data.error);
          setSuccessBanner({
            title: 'Payment received',
            desc: 'Your plan will update shortly.',
          });
          showToast('Payment received! Your plan will update shortly.', 'info');
        }
      } catch (err) {
        console.error(`${endpoint} verify error:`, err);
        setSuccessBanner({
          title: 'Payment received',
          desc: 'Your plan will update shortly.',
        });
        showToast('Payment received! Your plan will update shortly.', 'info');
      }
    };

    verify();
  }, [showToast, user?.email, user?.id]);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setLoading(false);
      return;
    }

    let channel: ReturnType<typeof supabase.channel> | null = null;

    const init = async () => {

      const { data: sub } = await supabase
        .from('subscriptions')
        .select('plan, status, bills_limit, current_period_start, current_period_end')
        .eq('user_id', user.id)
        .single();

      setSubscription(sub ?? null);

      const { count } = await supabase
        .from('bills')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      setBillsUsed(count ?? 0);

      // Sum amount_saved across all closed bills for this user. Cheap query
      //, most users have <10 bills, so client-side sum is fine. Anything
      // larger and we'd swap for a server-side aggregate / materialised view.
      const { data: savedRows } = await supabase
        .from('bills')
        .select('amount_saved')
        .eq('user_id', user.id)
        .eq('status', 'completed');

      const sum = (savedRows ?? []).reduce(
        (acc, row) => acc + (Number(row.amount_saved) || 0),
        0,
      );
      setTotalSaved(sum);

      /* Latest EOB review status drives the EOB-plan card copy below.
         Order by newest so a re-submission overrides an old report. */
      const { data: eobRow } = await supabase
        .from('eob_reviews')
        .select('status')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      setEobStatus(
        (eobRow?.status as
          | 'submitted'
          | 'in_review'
          | 'report_ready'
          | undefined) ?? null,
      );

      const { count: apCount } = await supabase
        .from('action_plans')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id);
      setActionPlanCount(apCount ?? 0);

      if (sub?.plan === 'action_plan') {
        try {
          const { data: sess } = await supabase.auth.getSession();
          const token = sess?.session?.access_token;
          const res = await fetch(`${API_BASE}/stripe/action-plan-credit`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (res.ok) {
            const credit = await res.json();
            setApCredit({ eligible: !!credit.eligible, deadline: credit.deadline ?? null });
          } else {
            setApCredit({ eligible: false, deadline: null });
          }
        } catch (err) {
          console.warn('action-plan-credit lookup failed:', err);
          setApCredit({ eligible: false, deadline: null });
        }
      }

      setLoading(false);

      channel = supabase
        .channel(`subscription-${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'subscriptions',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            setSubscription(payload.new as Subscription);
          }
        )
        .subscribe();
    };

    init();
    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [authLoading, user]);

  /* Get auth headers for protected endpoints */
  const getAuthHeaders = async () => {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  /* Handle "Add a Bill" checkout */
  const handleAddBill = async () => {
    if (!user || addonLoading) return;

    setAddonLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/stripe/create-addon-checkout`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        // Forward Meta CAPI tracking context so the Stripe webhook can
        // fire an authoritative server-side Purchase event with the
        // same browser identifiers Meta saw on the ad click.
        body: JSON.stringify(getMetaTrackingContext()),
      });

      const data = await res.json();

      if (!res.ok) {
        console.error('Addon checkout error:', data.error);
        phFailure({
          step: 'checkout_create_addon',
          message: data.error,
          code: res.status,
        });
        showToast(data.error || 'Could not start checkout. Please try again.', 'error');
        return;
      }

      // Meta: also fire InitiateCheckout via pixel + CAPI so Meta can
      // optimize ads against this in-app re-engagement signal. The eventual
      // Purchase fire on the success URL uses Stripe's session_id; this
      // event uses a fresh UUID since we don't yet have the session_id.
      const isActionPlanAccount = subscription?.plan === 'action_plan';
      const addonCheckoutId = crypto.randomUUID();
      const addonValue = isActionPlanAccount ? 49 : 129;
      phInitiateCheckout('addon', {
        flow: isActionPlanAccount ? 'action_plan_addon' : 'addon_bill',
        value: addonValue,
        plan: subscription?.plan ?? undefined,
      });
      void trackInitiateCheckout({
        eventID: addonCheckoutId,
        value: addonValue,
        contentName: 'addon_bill',
        email: user.email || undefined,
        externalId: user.id,
      });
      // Reddit: AddToCart is Reddit's documented mapping for "user
      // initiated checkout" (no InitiateCheckout in the standard set).
      trackRedditAddToCart({
        eventID: addonCheckoutId,
        value: addonValue,
        contentName: 'addon_bill',
        email: user.email || undefined,
        externalId: user.id,
      });
      window.location.href = data.url;
    } catch (err) {
      console.error('Addon checkout failed:', err);
      showToast('Something went wrong. Please try again.', 'error');
    } finally {
      setAddonLoading(false);
    }
  };

  /* Handle EOB → Pay Per Bill upgrade. Calls the authenticated
   * /stripe/create-eob-upgrade-checkout endpoint, which verifies the user
   * has an active EOB subscription and applies a $69 coupon to the $129
   * Per Bill price (customer pays $60). The webhook then transitions the
   * user's existing subscription row from 'eob' to 'one_off'. */
  const handleEobToOneOff = async () => {
    if (!user || upgradeLoading) return;

    setUpgradeLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/stripe/create-eob-upgrade-checkout`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(getMetaTrackingContext()),
      });

      const json = await res.json();
      if (!res.ok || !json?.url) {
        console.error('EOB → one_off checkout error:', json?.error);
        phFailure({
          step: 'checkout_create_eob_to_one_off',
          message: json?.error,
          code: res.status,
        });
        showToast(json?.error || 'Could not start checkout. Please try again.', 'error');
        return;
      }

      /* 'upgrade' is the closest semantic match in the existing posthog type
         union, this flow upgrades an EOB customer to Per Bill. `flow`
         (2026-08-08) carries the specific path: eob_to_one_off,
         eob_to_annual and one_off_to_annual all reported as plain 'upgrade'
         and were indistinguishable in PostHog, even though the Meta leg right
         below already passes a distinct contentName for each. */
      phInitiateCheckout('upgrade', {
        flow: 'eob_to_one_off',
        value: 60,
        plan: subscription?.plan ?? undefined,
      });
      const eobUpgradeId = crypto.randomUUID();
      void trackInitiateCheckout({
        eventID: eobUpgradeId,
        value: 60, // $129 Per Bill − $69 EOB credit
        contentName: 'eob_to_one_off',
        email: user.email || undefined,
        externalId: user.id,
      });
      trackRedditAddToCart({
        eventID: eobUpgradeId,
        value: 60,
        contentName: 'eob_to_one_off',
        email: user.email || undefined,
        externalId: user.id,
      });
      window.location.href = json.url;
    } catch (err) {
      console.error('EOB → one_off checkout failed:', err);
      showToast('Something went wrong. Please try again.', 'error');
    } finally {
      setUpgradeLoading(false);
    }
  };

  /* Personalized Action Plan → Pay Per Bill or Annual. The server decides whether the
     $49 credit still applies (14 days from paying for the plan) and prices
     the checkout accordingly; we only report the outcome. */
  const handleActionPlanUpgrade = async (target: 'one_off' | 'annual') => {
    if (!user || upgradeLoading) return;

    setUpgradeLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/stripe/create-action-plan-upgrade-checkout`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ target, ...getMetaTrackingContext() }),
      });

      const json = await res.json();
      const flow = target === 'annual' ? 'action_plan_to_annual' : 'action_plan_to_one_off';
      if (!res.ok || !json?.url) {
        console.error('Personalized Action Plan upgrade checkout error:', json?.error);
        phFailure({
          step: `checkout_create_${flow}`,
          message: json?.error,
          code: res.status,
        });
        showToast(json?.error || 'Could not start checkout. Please try again.', 'error');
        return;
      }

      const value = (target === 'annual' ? 499 : 129) - (json.credit_applied ? 49 : 0);
      phInitiateCheckout('upgrade', {
        flow,
        value,
        plan: subscription?.plan ?? undefined,
      });
      const checkoutId = crypto.randomUUID();
      void trackInitiateCheckout({
        eventID: checkoutId,
        value,
        contentName: flow,
        email: user.email || undefined,
        externalId: user.id,
      });
      trackRedditAddToCart({
        eventID: checkoutId,
        value,
        contentName: flow,
        email: user.email || undefined,
        externalId: user.id,
      });
      window.location.href = json.url;
    } catch (err) {
      console.error('Personalized Action Plan upgrade checkout failed:', err);
      showToast('Something went wrong. Please try again.', 'error');
    } finally {
      setUpgradeLoading(false);
    }
  };

  /* A free account picking a plan here (2026-10-27). Same checkout as the
     "Let's get the work started" button on their bill page, so the bill
     they've uploaded becomes their case once they've paid. */
  const [freePlanLoading, setFreePlanLoading] = useState<StartCasePlan | null>(null);
  const handleFreePlan = async (plan: StartCasePlan) => {
    if (!user || freePlanLoading) return;
    setFreePlanLoading(plan);
    phInitiateCheckout('upgrade', { flow: `free_to_${plan}`, value: plan === 'annual' ? 499 : 129, plan: 'free' });
    try {
      await startCaseCheckout(plan, user.email);
    } catch (err) {
      phFailure({ step: 'checkout_create_free_plan_page', message: (err as Error).message });
      showToast('Something went wrong. Please try again.', 'error');
      setFreePlanLoading(null);
    }
  };

  /* Handle "Upgrade to Annual" checkout */
  const handleUpgrade = async () => {
    if (!user || upgradeLoading) return;

    setUpgradeLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/stripe/create-upgrade-checkout`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(getMetaTrackingContext()),
      });

      const data = await res.json();

      if (!res.ok) {
        console.error('Upgrade checkout error:', data.error);
        phFailure({
          step: 'checkout_create_upgrade_annual',
          message: data.error,
          code: res.status,
        });
        showToast(data.error || 'Upgrade failed', 'error');
        return;
      }

      // Net cost depends on previous plan: one_off→annual = $120, eob→annual = $180.
      const fromEob = subscription?.plan === 'eob';
      const annualUpgradeId = crypto.randomUUID();
      const annualUpgradeValue = fromEob ? 180 : 120;
      phInitiateCheckout('upgrade', {
        flow: fromEob ? 'eob_to_annual' : 'one_off_to_annual',
        value: annualUpgradeValue,
        plan: subscription?.plan ?? undefined,
      });
      void trackInitiateCheckout({
        eventID: annualUpgradeId,
        value: annualUpgradeValue,
        contentName: 'upgrade_to_annual',
        email: user.email || undefined,
        externalId: user.id,
      });
      trackRedditAddToCart({
        eventID: annualUpgradeId,
        value: annualUpgradeValue,
        contentName: 'upgrade_to_annual',
        email: user.email || undefined,
        externalId: user.id,
      });
      window.location.href = data.url;
    } catch (err) {
      console.error('Upgrade checkout failed:', err);
      showToast('Something went wrong. Please try again.', 'error');
    } finally {
      setUpgradeLoading(false);
    }
  };

  /* Cancel the annual subscription at period end. Invoked only from the
     explicit Cancel flow (the dedicated "Cancel subscription" button), not
     from any "switch/downgrade" affordance. */
  const handleDowngrade = async () => {
    if (!user || downgradeLoading) return;

    setDowngradeLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/stripe/cancel-subscription`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          reason: cancelReason || null,
          comment: cancelComment.trim() || null,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        console.error('Cancel error:', data.error);
        phFailure({
          step: 'subscription_cancel',
          message: data.error,
          code: res.status,
        });
        showToast(data.error || 'Cancellation failed', 'error');
        return;
      }

      setShowDowngradeConfirm(false);
      /* 2026-08-08: the cancel modal has always collected a reason and sent it
         to the server (which stores it on subscriptions.cancellation_reason,
         migration 054 — added precisely because "churn was a black box"), but
         phPlanChange only ever took an action string, so PostHog could not
         break cancellations down by reason. Now it can. */
      phPlanChange('downgrade', {
        reason: cancelReason || undefined,
        comment: cancelComment,
        plan: subscription?.plan ?? undefined,
      });
      // Meta: custom event so we can suppress these users from lookalike
      // audiences. event_id is user.id + timestamp so a re-cancellation
      // (rare) doesn't get deduped against an earlier one.
      void trackCustomEvent({
        name: 'SubscriptionCancelled',
        eventID: `${user.id}-${Date.now()}`,
        customData: { plan: 'annual', cancellation_type: 'cancel_at_period_end' },
        email: user.email || undefined,
      });

      // Update local state so the UI reflects the canceled status immediately
      setSubscription((prev) =>
        prev
          ? {
              ...prev,
              status: 'canceled',
              current_period_end: data.current_period_end ?? prev.current_period_end,
            }
          : prev
      );

      setSuccessBanner({
        title: 'Subscription canceled',
        desc: `Your annual plan will remain active until ${
          data.current_period_end
            ? new Date(data.current_period_end).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })
            : 'the end of your billing period'
        }. After that, you can purchase individual bills.`,
      });
      showToast('Annual plan will not renew.', 'info');
    } catch (err) {
      console.error('Downgrade failed:', err);
      showToast('Something went wrong. Please try again.', 'error');
    } finally {
      setDowngradeLoading(false);
    }
  };

  /* Handle "Reactivate" subscription */
  const handleReactivate = async () => {
    if (!user || reactivateLoading) return;

    setReactivateLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/stripe/reactivate-subscription`, {
        method: 'POST',
        headers,
      });

      const data = await res.json();

      if (!res.ok) {
        showToast(data.error || 'Reactivation failed', 'error');
        return;
      }

      // Refresh subscription data
      const { data: sub } = await supabase
        .from('subscriptions')
        .select('plan, status, bills_limit, current_period_start, current_period_end')
        .eq('user_id', user.id)
        .single();

      if (sub) setSubscription(sub);

      phPlanChange('reactivate', { plan: subscription?.plan ?? undefined });
      setSuccessBanner({
        title: 'Subscription reactivated',
        desc: 'Your annual plan will continue to renew automatically.',
      });
      showToast('Annual plan reactivated!', 'success');
    } catch (err) {
      console.error('Reactivate failed:', err);
      showToast('Something went wrong. Please try again.', 'error');
    } finally {
      setReactivateLoading(false);
    }
  };

  if (loading) return <GlobalLoader message="Loading your plan details…" />;

  // Personalized Action Plan accounts (2026-08-28): the plan card, then the negotiation
  // upsell. Within 14 days of paying for the plan the $49 comes off Pay Per
  // Bill or Annual; the server owns that rule (actionPlanCredit.ts), this
  // page only prices the cards from its answer.
  if (subscription?.plan === 'action_plan') {
    const plansPaid = subscription.bills_limit ?? 0;
    const plansLeft = Math.max(plansPaid - actionPlanCount, 0);
    const refunded = subscription.status === 'refunded';
    const creditOpen = apCredit?.eligible === true;
    const creditUntil = creditOpen && apCredit?.deadline
      ? new Date(apCredit.deadline).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
      : null;
    return (
      <div className="dashboard-container">
        <main className="plan-content">
          <h1 className="plan-heading">Plan &amp; billing</h1>

          {successBanner && (
            <div className="addon-success-banner">
              <div className="addon-success-content">
                <CheckCircle size={20} className="addon-success-icon" />
                <div>
                  <p className="addon-success-title">{successBanner.title}</p>
                  <p className="addon-success-desc">{successBanner.desc}</p>
                </div>
              </div>
              <button
                className="addon-success-dismiss"
                onClick={() => setSuccessBanner(null)}
                aria-label="Dismiss"
              >
                <X size={16} />
              </button>
            </div>
          )}

          <div className="plan-card">
            <div className="plan-card-header">
              <div>
                <div className="plan-name-row">
                  <h2 className="plan-name">Personalized Action Plan</h2>
                  <span className="active-badge">{refunded ? 'Refunded' : 'Active'}</span>
                </div>
                <p style={{ fontSize: '13px', color: 'rgba(26,18,48,0.7)', marginTop: '6px', marginBottom: 0 }}>
                  {plansPaid} plan{plansPaid === 1 ? '' : 's'} paid for, {actionPlanCount} sent in
                  {plansLeft > 0 ? `, ${plansLeft} ready to use.` : '.'}{' '}
                  <Link
                    to="/action-plan"
                    style={{ color: 'var(--color-fox-deep)', fontWeight: 500, textDecoration: 'underline', textUnderlineOffset: '2px' }}
                  >
                    {plansLeft > 0 ? 'Send in a case file' : 'Open your plan'}
                  </Link>
                </p>
              </div>
            </div>
          </div>

          {!refunded && (
            <>
              <div style={{ margin: '32px 0 20px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#0E1530', margin: '0 0 8px' }}>
                  Rather have us handle it?
                </h2>
                <p style={{ fontSize: '14px', color: 'rgba(26,18,48,0.65)', lineHeight: 1.6, margin: 0 }}>
                  A specialist negotiates the bill for you, start to finish.
                  {creditUntil && (
                    <> Upgrade by {creditUntil} and the $49 you paid for your plan comes off the price.</>
                  )}
                </p>
              </div>

              <div className="eob-upsell-grid">
                <article className="eob-upsell-card">
                  <div className="eob-upsell-card-label">Pay Per Bill</div>
                  <div className="eob-upsell-card-price-row">
                    <span className="eob-upsell-card-amount">{creditOpen ? '$80' : '$129'}</span>
                    {creditOpen && <span className="eob-upsell-card-was">$129</span>}
                  </div>
                  <p className="eob-upsell-card-meta">
                    {creditOpen ? '$49 Personalized Action Plan credit applied' : 'One bill, negotiated for you'}
                  </p>
                  <p className="eob-upsell-card-desc">
                    One bill, worked by a specialist from the first call to the final balance.
                  </p>
                  <button
                    className="eob-upsell-card-btn outline"
                    onClick={() => handleActionPlanUpgrade('one_off')}
                    disabled={upgradeLoading}
                  >
                    {upgradeLoading ? 'Redirecting…' : 'Have us negotiate this bill'}
                  </button>
                  <div className="eob-upsell-card-reassure">
                    <span className="eob-upsell-card-reassure-icon" aria-hidden>✓</span>
                    No commitment beyond this bill
                  </div>
                </article>

                <article className="eob-upsell-card recommended">
                  <div className="eob-upsell-card-ribbon">Best for families</div>
                  <div className="eob-upsell-card-label">Annual</div>
                  <div className="eob-upsell-card-price-row">
                    <span className="eob-upsell-card-amount">{creditOpen ? '$450' : '$499'}</span>
                    {creditOpen && <span className="eob-upsell-card-was">$499</span>}
                  </div>
                  <p className="eob-upsell-card-meta">
                    {creditOpen ? '$49 credit applied, 6 negotiations a year' : '6 negotiations a year'}
                  </p>
                  <p className="eob-upsell-card-desc">
                    Covers the whole household all year, front of the queue, phone and email support.
                  </p>
                  <button
                    className="eob-upsell-card-btn"
                    onClick={() => handleActionPlanUpgrade('annual')}
                    disabled={upgradeLoading}
                  >
                    {upgradeLoading ? 'Redirecting…' : 'Upgrade to Annual'}
                  </button>
                  <div className="eob-upsell-card-reassure">
                    <span className="eob-upsell-card-reassure-icon" aria-hidden>✓</span>
                    Cancel anytime
                  </div>
                </article>
              </div>

              <p style={{ fontSize: '14px', color: 'rgba(26,18,48,0.65)', lineHeight: 1.6, margin: '28px 0 0' }}>
                Have another bill? A plan for it is $49.{' '}
                <button
                  type="button"
                  onClick={handleAddBill}
                  disabled={addonLoading}
                  style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'var(--color-fox-deep)', fontWeight: 500, textDecoration: 'underline', textUnderlineOffset: '2px', cursor: 'pointer', boxShadow: 'none' }}
                >
                  {addonLoading ? 'Opening checkout…' : 'Get a plan for another bill'}
                </button>
              </p>
            </>
          )}
        </main>
      </div>
    );
  }

  // EOB plan users see a dedicated upgrade screen — two pricing cards
  // (Pay Per Bill + Annual recommended), avatar testimonial below, trust line.
  if (subscription?.plan === 'eob') {
    return (
      <div className="dashboard-container">
        <main className="plan-content">
          <h1 className="plan-heading">Plan &amp; billing</h1>

          {/* EOB Plan card — copy + CTA reflect the latest eob_reviews
              row's status so we don't keep telling the user we're
              reviewing after the report has actually shipped. */}
          <div className="plan-card">
            <div className="plan-card-header">
              <div>
                <div className="plan-name-row">
                  <h2 className="plan-name">EOB Review</h2>
                  {eobStatus === 'report_ready' ? (
                    <span className="active-badge">Report ready</span>
                  ) : eobStatus === 'in_review' ? (
                    <span className="active-badge">In review</span>
                  ) : eobStatus === 'submitted' ? (
                    <span className="active-badge">Submitted</span>
                  ) : (
                    <span className="active-badge">Active</span>
                  )}
                </div>
                {eobStatus === 'report_ready' ? (
                  <p style={{ fontSize: '13px', color: 'rgba(26,18,48,0.7)', marginTop: '6px', marginBottom: 0 }}>
                    Your report is ready.{' '}
                    <Link
                      to="/eob"
                      style={{ color: 'var(--color-fox-deep)', fontWeight: 500, textDecoration: 'underline', textUnderlineOffset: '2px' }}
                    >
                      View what Agent Loop found →
                    </Link>
                  </p>
                ) : eobStatus === 'submitted' || eobStatus === 'in_review' ? (
                  <p style={{ fontSize: '13px', color: 'rgba(26,18,48,0.5)', marginTop: '6px', marginBottom: 0 }}>
                    Agent Loop is reviewing your Explanation of Benefits.
                    You'll receive an email when your report is ready.
                  </p>
                ) : (
                  <p style={{ fontSize: '13px', color: 'rgba(26,18,48,0.5)', marginTop: '6px', marginBottom: 0 }}>
                    Upload your Explanation of Benefits to get started.{' '}
                    <Link
                      to="/eob"
                      style={{ color: 'var(--color-fox-deep)', fontWeight: 500, textDecoration: 'underline', textUnderlineOffset: '2px' }}
                    >
                      Send in your EOB →
                    </Link>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Upsell intro, extra top/bottom margin to breathe between the
              EOB Active card and the pricing grid below. */}
          <div style={{ margin: '32px 0 20px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#0E1530', margin: '0 0 8px' }}>
              Want Agent Loop to fight your bills too?
            </h2>
            <p style={{ fontSize: '14px', color: 'rgba(26,18,48,0.65)', lineHeight: 1.6, margin: 0 }}>
              Upgrade to full bill negotiation, average savings of 60 to 80%. Your $69 EOB review is credited toward Pay Per Bill.
            </p>
          </div>

          {/* Pricing cards — shared design with /eob */}
          <div className="eob-upsell-grid">
            {/* ── Pay Per Bill ── */}
            <article className="eob-upsell-card">
              <div className="eob-upsell-card-label">Pay Per Bill</div>
              <div className="eob-upsell-card-price-row">
                <span className="eob-upsell-card-amount">$60</span>
                <span className="eob-upsell-card-was">$129</span>
              </div>
              <p className="eob-upsell-card-meta">$69 EOB credit applied</p>
              <p className="eob-upsell-card-desc">
                Full negotiation on one medical bill. Money-back if no savings.
              </p>
              <button
                className="eob-upsell-card-btn outline"
                onClick={handleEobToOneOff}
                disabled={upgradeLoading}
              >
                {upgradeLoading ? 'Redirecting…' : 'Start bill negotiation'}
              </button>
              <div className="eob-upsell-card-reassure">
                <span className="eob-upsell-card-reassure-icon" aria-hidden>✓</span>
                No commitment · Money-back if no savings
              </div>
            </article>

            {/* ── Annual (recommended) — integrated "Most popular" ribbon ── */}
            <article className="eob-upsell-card recommended">
              <div className="eob-upsell-card-ribbon">Most popular</div>
              <div className="eob-upsell-card-label">Annual</div>
              <div className="eob-upsell-card-price-row">
                <span className="eob-upsell-card-amount">$430</span>
                <span className="eob-upsell-card-was">$499</span>
              </div>
              <p className="eob-upsell-card-meta">$69 EOB credit · save $275/year</p>
              <p className="eob-upsell-card-desc">
                Six bill negotiations a year, unlimited EOB reviews, priority queue.
                Roughly $72 per bill.
              </p>
              <button
                className="eob-upsell-card-btn"
                onClick={handleUpgrade}
                disabled={upgradeLoading}
              >
                {upgradeLoading ? 'Redirecting…' : 'Upgrade to Annual'}
              </button>
              <div className="eob-upsell-card-reassure">
                <span className="eob-upsell-card-reassure-icon" aria-hidden>✓</span>
                Cancel anytime · Same money-back guarantee
              </div>
            </article>
          </div>
        </main>
      </div>
    );
  }

  /* Derive plan display values */
  const planName = subscription
    ? subscription.plan === 'annual'
      ? 'Annual Plan'
      : 'Per Bill'
    : 'Free';
  const planPrice = subscription
    ? subscription.plan === 'annual'
      ? 499
      : 0
    : 0;
  const billingPeriod = subscription?.plan === 'annual' ? 'year' : 'month';
  const isActive = subscription?.status === 'active';
  const isCanceled = subscription?.status === 'canceled';
  const isRefunded = subscription?.status === 'refunded';
  const billsLimit = subscription?.bills_limit ?? 0;
  const billsRemaining = Math.max(billsLimit - billsUsed, 0);
  const isAnnual = subscription?.plan === 'annual';
  const addonPrice = 129;

  // Format the renewal/expiry date for annual plans
  const renewalDate = isAnnual && subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  // Refunded customers see a dedicated screen, no plan controls shown
  if (isRefunded) {
    return (
      <div className="dashboard-container">
        <main className="plan-content">
          <h1 className="plan-heading">Plan &amp; billing</h1>
          <div style={{
            background: '#fff8f8',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            padding: '28px 24px',
            marginTop: '16px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>💸</div>
            <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#0E1530', marginBottom: '8px' }}>
              Your refund has been processed
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6', maxWidth: '380px', margin: '0 auto 20px' }}>
              Your payment has been refunded. If you have questions about your refund or would like to start a new case, please reach out to our team.
            </p>
            <a
              href="mailto:help@mediloop.ai"
              style={{
                display: 'inline-block',
                background: 'linear-gradient(to right, #E97A50, #C8542A)',
                color: '#fff',
                padding: '10px 24px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Contact Us
            </a>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <main className="plan-content">
        <h1 className="plan-heading">Plan &amp; billing</h1>

        {/* Success Banner */}
        {successBanner && (
          <div className="addon-success-banner">
            <div className="addon-success-content">
              <CheckCircle size={20} className="addon-success-icon" />
              <div>
                <p className="addon-success-title">{successBanner.title}</p>
                <p className="addon-success-desc">{successBanner.desc}</p>
              </div>
            </div>
            <button
              className="addon-success-dismiss"
              onClick={() => setSuccessBanner(null)}
              aria-label="Dismiss"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Plan Card. A free account has nothing to count yet ("0 of 0
            bills, $0.00 saved" read like an error), so it gets one plain
            line instead (Oct 2026). */}
        {!subscription ? (
          <div className="plan-free-note">
            <p className="plan-free-title">You're on a free account</p>
            <p className="plan-free-body">
              Uploading a bill and seeing what we find is free. Pick a plan below when you
              want a specialist to work on it.
            </p>
          </div>
        ) : (
        <div className="plan-card">
          <div className="plan-card-header">
            <div>
              <div className="plan-name-row">
                <h2 className="plan-name">{planName}</h2>
                {isActive && !isCanceled && <span className="active-badge">Active</span>}
                {isCanceled && <span className="active-badge canceled-badge">Cancelled</span>}
              </div>
              {isAnnual && renewalDate && (
                <p className={`plan-renewal ${isCanceled ? 'plan-renewal-canceled' : ''}`}>
                  {isCanceled ? `Cancels on ${renewalDate}` : `Renews on ${renewalDate}`}
                </p>
              )}
            </div>
            {isAnnual && (
              <div className="plan-price-col">
                <span className="plan-price">${planPrice}</span>
                <span className="plan-period">/{billingPeriod}</span>
              </div>
            )}
          </div>

          <div className="plan-stats">
            {/* Left — Bills Used with a segmented pill bar (one slot per
                bill in the plan). More visual than a single progress bar
                and lets you "see" each slot at a glance. */}
            <div className="plan-usage-section">
              <div className="plan-usage-label">Bills used</div>
              <div className="plan-usage-hero">
                <span className="plan-usage-big num">{billsUsed}</span>
                <span className="plan-usage-of">of</span>
                <span className="plan-usage-limit num">{billsLimit}</span>
              </div>
              <div
                className="plan-usage-pips"
                role="img"
                aria-label={`${billsUsed} of ${billsLimit} bills used`}
              >
                {Array.from({ length: billsLimit }).map((_, i) => (
                  <span
                    key={i}
                    className={`plan-usage-pip ${i < billsUsed ? 'is-used' : ''}`}
                    aria-hidden
                  />
                ))}
              </div>
              <p className="plan-remaining">
                <span className="num">{billsRemaining}</span>{' '}
                bill{billsRemaining !== 1 ? 's' : ''} remaining
                {renewalDate && (
                  <>
                    <span className="plan-remaining-dot" aria-hidden> · </span>
                    Renews {renewalDate}
                  </>
                )}
              </p>
            </div>

            {/* Right — always renders. Shows the running savings tally if
                any cases have closed with savings; otherwise gives an empty
                state so the column has structure instead of negative space. */}
            <div className="plan-saved-section">
              <div className="plan-saved-label">Saved with mediloop</div>
              {totalSaved !== null && totalSaved > 0 ? (
                <>
                  <div className="plan-saved-amount num">
                    ${totalSaved.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                  {planPrice > 0 && totalSaved > planPrice && (
                    <div className="plan-saved-roi">
                      <span className="italic">
                        {(totalSaved / planPrice).toFixed(1)}× what you paid.
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="plan-saved-amount plan-saved-amount--zero num">
                    $0.00
                  </div>
                  <div className="plan-saved-roi">
                    <span className="italic">
                      Your savings will show up here as cases close.
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {(isActive || isCanceled) && (
            <div className="plan-buy-more">
              <p>Need more? Add a bill for ${addonPrice}.</p>
              <button
                className="buy-more-btn"
                onClick={handleAddBill}
                disabled={addonLoading}
              >
                {addonLoading ? 'Redirecting...' : 'Add a Bill'}
              </button>
            </div>
          )}
        </div>
        )}

        {/* Pricing Cards */}
        <div className="pricing-grid">
          {/* Per Bill Card */}
          <div className={`pricing-card ${!isAnnual ? 'current' : ''}`}>
            <h3 className="pricing-title">Per Bill</h3>
            <span className="pricing-amount">$129</span>
            <span className="pricing-sub">per bill</span>
            <ul className="pricing-features">
              <li>
                <Check size={16} className="feature-check" />
                Pay as you go
              </li>
              <li>
                <Check size={16} className="feature-check" />
                Full service
              </li>
              <li>
                <Check size={16} className="feature-check" />
                No commitment
              </li>
            </ul>
            {!isAnnual && subscription ? (
              <div className="current-plan-indicator">Your current plan</div>
            ) : !subscription ? (
              <button
                className="pricing-cta pricing-cta-outline"
                onClick={() => void handleFreePlan('one_off')}
                disabled={freePlanLoading !== null}
              >
                {freePlanLoading === 'one_off' ? 'Redirecting…' : 'Choose Per Bill'}
              </button>
            ) : isCanceled ? (
              <div className="current-plan-indicator canceled-indicator">
                Switching after period ends
              </div>
            ) : (
              <div className="current-plan-indicator current-plan-indicator--muted">
                Pay-as-you-go
              </div>
            )}
          </div>

          {/* Annual Plan Card */}
          <div className={`pricing-card highlighted ${isAnnual ? 'current' : ''}`}>
            <span className="best-value-badge">Best Value</span>
            <h3 className="pricing-title">Annual Plan</h3>
            <span className="pricing-amount">$499</span>
            <span className="pricing-sub">6 bills included</span>
            <ul className="pricing-features">
              <li>
                <Check size={16} className="feature-check" />
                ~$83 per bill
              </li>
              <li>
                <Check size={16} className="feature-check" />
                Priority support
              </li>
            </ul>
            {isAnnual && isCanceled ? (
              <button
                className="pricing-cta"
                onClick={handleReactivate}
                disabled={reactivateLoading}
              >
                {reactivateLoading ? 'Reactivating...' : 'Reactivate Annual Plan'}
              </button>
            ) : isAnnual ? (
              <div className="current-plan-indicator">Your current plan</div>
            ) : !subscription ? (
              <button
                className="pricing-cta"
                onClick={() => void handleFreePlan('annual')}
                disabled={freePlanLoading !== null}
              >
                {freePlanLoading === 'annual' ? 'Redirecting…' : 'Choose Annual'}
              </button>
            ) : (
              <button
                className="pricing-cta"
                onClick={handleUpgrade}
                disabled={upgradeLoading}
              >
                {upgradeLoading ? 'Redirecting...' : 'Upgrade to Annual'}
              </button>
            )}
          </div>
        </div>

        {/* Referral (give $40, get $25) — page-level block so it's visible
            on mobile too, where the sidebar (and its ReferralCard) is
            hidden entirely. Renders nothing for non-payers. */}
        <ReferralPanel />

        {/* Manage subscription — a clearly-defined section at the bottom of
            the page (the conventional home for a destructive, low-frequency
            action). Defined enough to be unmistakable once scrolled to,
            without competing with the plan summary above. */}
        {isAnnual && isActive && !isCanceled && (
          <div className="plan-manage-section">
            <div className="plan-manage-text">
              <div className="plan-manage-title">Cancel subscription</div>
              <div className="plan-manage-sub">
                You'll keep your Annual benefits until your renewal date and won't be charged again.
              </div>
            </div>
            <button
              type="button"
              className="plan-manage-cancel-btn"
              onClick={openCancelFlow}
            >
              Cancel subscription
            </button>
          </div>
        )}

        {/* Cancel flow — two steps: (1) why are you leaving?  (2) save
            attempt before we actually cancel at period end. */}
        {showDowngradeConfirm && (
          <div className="downgrade-overlay" onClick={() => setShowDowngradeConfirm(false)}>
            <div className="downgrade-modal" onClick={(e) => e.stopPropagation()}>

              {cancelStep === 'reason' ? (
                <>
                  <h3 className="downgrade-modal-title">Before you go</h3>
                  <p className="downgrade-modal-desc">
                    We'd love to know what's prompting this. It helps us get better.
                  </p>

                  <div className="cancel-reason-list">
                    {CANCEL_REASONS.map((r) => (
                      <label
                        key={r.value}
                        className={`cancel-reason-option ${cancelReason === r.value ? 'is-selected' : ''}`}
                      >
                        <input
                          type="radio"
                          name="cancel-reason"
                          value={r.value}
                          checked={cancelReason === r.value}
                          onChange={() => setCancelReason(r.value)}
                        />
                        <span>{r.label}</span>
                      </label>
                    ))}
                  </div>

                  <textarea
                    className="cancel-reason-comment"
                    placeholder="Anything else you'd like us to know? (optional)"
                    value={cancelComment}
                    onChange={(e) => setCancelComment(e.target.value)}
                    rows={3}
                    maxLength={1000}
                  />

                  <div className="downgrade-modal-actions">
                    <button
                      className="dg-btn dg-btn-ghost"
                      onClick={() => setShowDowngradeConfirm(false)}
                    >
                      Keep my plan
                    </button>
                    <button
                      className="dg-btn dg-btn-primary"
                      onClick={() => setCancelStep('save')}
                      disabled={!cancelReason}
                    >
                      Continue
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="downgrade-modal-title">A couple of things first</h3>
                  <p className="downgrade-modal-desc">
                    If you cancel, your Annual plan stays active until{' '}
                    <strong>{renewalDate || 'the end of your billing period'}</strong>.{' '}
                    You keep every benefit until then and you won't be charged again.
                    You can reactivate anytime with one click before that date.
                  </p>
                  {totalSaved !== null && totalSaved > 0 && (
                    <p className="downgrade-modal-desc">
                      So far Agent Loop has saved you{' '}
                      <strong>
                        ${totalSaved.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </strong>
                      . Your next bill could be the one that pays for the year.
                    </p>
                  )}
                  <p className="downgrade-modal-desc">
                    After your plan ends you'll switch to pay-as-you-go at $129 per bill.
                  </p>

                  <div className="downgrade-modal-actions">
                    <button
                      className="dg-btn dg-btn-danger-ghost"
                      onClick={handleDowngrade}
                      disabled={downgradeLoading}
                    >
                      {downgradeLoading ? 'Cancelling…' : 'Cancel anyway'}
                    </button>
                    <button
                      className="dg-btn dg-btn-primary"
                      onClick={() => setShowDowngradeConfirm(false)}
                    >
                      Keep my plan
                    </button>
                  </div>
                </>
              )}

            </div>
          </div>
        )}
      </main>
    </div>
  );
}
